import React, { useState } from 'react';
import classNames from 'classnames';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { compose } from 'redux';
import { connect } from 'react-redux';

import {
  Page,
  LayoutSingleColumn,
  PrimaryButton,
  NamedLink,
  H1,
  H2,
  H3,
} from '../../components';
import TopbarSpacer from '../TopbarContainer/TopbarSpacer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './HowItWorksPage.module.css';

const SELLERS = 'sellers';
const BUYERS = 'buyers';

const StepCard = ({ number, title, children }) => (
  <li className={css.stepCard}>
    <div className={css.stepNumber}>{number}</div>
    <div className={css.stepBody}>
      <H3 className={css.stepTitle}>{title}</H3>
      <div className={css.stepText}>{children}</div>
    </div>
  </li>
);

const SellersContent = () => (
  <>
    <p className={css.lede}>
      Get the best return on your design icons without the hassle. We handle
      the valuation, pricing, pickup and shipping — you wait for the money to
      land.
    </p>

    <ol className={css.stepList}>
      <StepCard number="1" title="List your item — free">
        Upload a few photos and details. Takes minutes. No listing fee, no
        minimum commitment.
      </StepCard>

      <StepCard number="2" title="We value it">
        Our in-house team of design experts reviews your listing and comes back
        with a professional valuation — usually within a few working days.
      </StepCard>

      <StepCard number="3" title="We set your Optimized Pricing">
        <p>
          Based on the valuation, we generate a pricing strategy tailored to
          help you sell as quickly as possible. Depending on the piece, that
          might be:
        </p>
        <ul className={css.nestedList}>
          <li>
            <strong>A single recommended price</strong> — when the market for
            your item is stable and well-known.
          </li>
          <li>
            <strong>A price schedule over several months</strong> — where the
            price adjusts automatically based on market signals, so you never
            leave money on the table or sit unsold for too long.
          </li>
        </ul>
        <p>
          You'll see exactly what you'll earn at each step before you accept
          anything.
        </p>
      </StepCard>

      <StepCard number="4" title="You accept — we publish">
        Review the pricing, accept with one click, and your listing goes live.
        Want to discuss it first? Send us a message directly from the pricing
        page.
      </StepCard>

      <StepCard number="5" title="We handle the sale">
        When a buyer comes along, we arrange pickup from your address (or from
        our warehouse, if you've opted into pre-sale storage). You don't lift
        a finger — literally.
      </StepCard>

      <StepCard number="6" title="You get paid">
        Once the item is delivered to the buyer, we transfer your earnings.{' '}
        <strong>Used Icons takes a flat 30% commission.</strong> No tiers, no
        packages, no hidden fees.
      </StepCard>
    </ol>

    <section className={css.callout}>
      <H3 className={css.calloutTitle}>Optional: Pre-sale storage</H3>
      <p>
        Moving? Downsizing? Just don't want a sofa in your hallway while you
        wait for it to sell? Opt into pre-sale storage and we'll look after
        your item in our warehouse until it finds a new home. Drop it off
        yourself, or let us pick it up.
      </p>
    </section>

    <section className={css.included}>
      <H3 className={css.includedTitle}>What's always included</H3>
      <ul className={css.checkList}>
        <li>Free listing and professional valuation</li>
        <li>Optimized Pricing tailored to your piece</li>
        <li>Arranged pickup when the item sells</li>
        <li>A dedicated team you can message anytime</li>
        <li>A flat 30% commission — no surprises</li>
      </ul>
    </section>

    <div className={css.ctaRow}>
      <NamedLink name="NewListingPage" className={css.ctaLink}>
        <PrimaryButton className={css.ctaButton}>
          Create your listing
        </PrimaryButton>
      </NamedLink>
    </div>
  </>
);

const BuyersContent = () => (
  <>
    <p className={css.lede}>
      Buy pre-owned design classics, properly vetted and fairly priced. Every
      item has been reviewed by our team — no fakes, no mystery condition, no
      overpriced guesswork.
    </p>

    <ol className={css.stepList}>
      <StepCard number="1" title="Browse">
        Explore our current drops, or search by designer, era or category. Every
        listing has been approved by our team and priced transparently.
      </StepCard>

      <StepCard number="2" title="Buy">
        Check out securely. We handle the transaction end-to-end.
      </StepCard>

      <StepCard number="3" title="Receive">
        We arrange delivery from the seller (or from our warehouse, if the piece
        is already stored with us) straight to you — with tracking the whole
        way.
      </StepCard>
    </ol>

    <section className={css.included}>
      <H3 className={css.includedTitle}>Why buy through Used Icons</H3>
      <ul className={css.checkList}>
        <li>
          <strong>Every item is reviewed.</strong> We verify condition and
          authenticity before anything goes live.
        </li>
        <li>
          <strong>Transparent pricing.</strong> Our Optimized Pricing reflects
          real market value — no inflated asking prices, no haggling theatre.
        </li>
        <li>
          <strong>Handled end-to-end.</strong> One checkout, one delivery, one
          team to talk to if anything comes up.
        </li>
      </ul>
    </section>

    <div className={css.ctaRow}>
      <NamedLink name="SearchPage" className={css.ctaLink}>
        <PrimaryButton className={css.ctaButton}>Start browsing</PrimaryButton>
      </NamedLink>
    </div>
  </>
);

const HowItWorksPageComponent = props => {
  const { scrollingDisabled } = props;
  const [activeTab, setActiveTab] = useState(SELLERS);

  return (
    <Page scrollingDisabled={scrollingDisabled} title="How it works">
      <LayoutSingleColumn
        className={css.layout}
        mainColumnClassName={css.mainColumn}
        topbar={<TopbarSpacer />}
        footer={<FooterContainer />}
      >
        <div className={css.hero}>
          <H1 className={css.heroTitle}>How it works</H1>
          <p className={css.heroSubtitle}>
            A marketplace for pre-owned design classics — handled end-to-end
            by us.
          </p>
        </div>

        <div className={css.content}>
          <div
            className={css.tabs}
            role="tablist"
            aria-label="How it works sections"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === SELLERS}
              className={classNames(css.tab, {
                [css.tabActive]: activeTab === SELLERS,
              })}
              onClick={() => setActiveTab(SELLERS)}
            >
              For Sellers
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === BUYERS}
              className={classNames(css.tab, {
                [css.tabActive]: activeTab === BUYERS,
              })}
              onClick={() => setActiveTab(BUYERS)}
            >
              For Buyers
            </button>
          </div>

          <div className={css.panel} role="tabpanel">
            {activeTab === SELLERS ? <SellersContent /> : <BuyersContent />}
          </div>

          <section className={css.questions}>
            <H2 className={css.questionsTitle}>Still have questions?</H2>
            <p>
              Shoot us a message — we're happy to walk you through any part of
              the process, whether you're about to list your first piece or
              just wondering what that credenza in the back of the garage might
              be worth.
            </p>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const HowItWorksPage = compose(connect(mapStateToProps))(HowItWorksPageComponent);

export default HowItWorksPage;
